<template>
  <div
    ref="containerRef"
    class="video-thumbnail-strip"
    :class="{
      'video-thumbnail-strip--horizontal': direction === 'horizontal',
      'video-thumbnail-strip--vertical': direction === 'vertical',
    }"
  >
    <!-- Prev/Next arrows (horizontal only) -->
    <button
      v-if="direction === 'horizontal' && hasOverflow"
      class="video-thumbnail-strip__arrow video-thumbnail-strip__arrow--left"
      aria-label="Previous"
      @click="scrollByPage(-1)"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
      </svg>
    </button>

    <div
      ref="scrollRef"
      class="video-thumbnail-strip__scroll"
      @scroll="onScroll"
      @mousedown="onMouseDown"
    >
      <!-- Clone of last N items (prepended for infinite loop) -->
      <template v-if="direction === 'horizontal' && hasOverflow">
        <button
          v-for="(item, i) in cloneTail"
          :key="'clone-tail-' + i"
          class="video-thumbnail-strip__item"
          tabindex="-1"
          aria-hidden="true"
          @click="onItemClick(items.length - cloneCount + i)"
        >
          <div class="video-thumbnail-strip__thumbnail">
            <img
              v-if="item.asset.thumbnailUrl"
              :src="withThumbnailSize(item.asset.thumbnailUrl, 640, 360, 'cover')"
              :alt="item.customTitle || item.asset.name"
              class="video-thumbnail-strip__image"
              loading="lazy"
            />
            <div v-else class="video-thumbnail-strip__placeholder" />
            <span v-if="item.asset.duration" class="video-thumbnail-strip__duration">
              {{ formatTime(item.asset.duration) }}
            </span>
          </div>
          <span class="video-thumbnail-strip__title">
            {{ item.customTitle || item.asset.name }}
          </span>
        </button>
      </template>

      <!-- Real items -->
      <button
        v-for="(item, index) in items"
        :key="item.asset.uid"
        :ref="(el) => setItemRef(index, el as HTMLElement | null)"
        class="video-thumbnail-strip__item"
        :class="{
          'video-thumbnail-strip__item--active': index === currentIndex,
        }"
        :aria-current="index === currentIndex ? 'true' : undefined"
        @click="onItemClick(index)"
      >
        <div class="video-thumbnail-strip__thumbnail">
          <img
            v-if="item.asset.thumbnailUrl"
            :src="withThumbnailSize(item.asset.thumbnailUrl, 640, 360, 'cover')"
            :alt="item.customTitle || item.asset.name"
            class="video-thumbnail-strip__image"
            loading="lazy"
          />
          <div v-else class="video-thumbnail-strip__placeholder" />
          <span v-if="item.asset.duration" class="video-thumbnail-strip__duration">
            {{ formatTime(item.asset.duration) }}
          </span>
          <!-- Now playing indicator (independent of primary color) -->
          <div
            v-if="index === currentIndex && isPlaying"
            class="video-thumbnail-strip__now-playing"
            aria-hidden="true"
          >
            <span class="video-thumbnail-strip__eq-bar" />
            <span class="video-thumbnail-strip__eq-bar" />
            <span class="video-thumbnail-strip__eq-bar" />
          </div>
        </div>
        <span class="video-thumbnail-strip__title">
          {{ item.customTitle || item.asset.name }}
        </span>
      </button>

      <!-- Clone of first N items (appended for infinite loop) -->
      <template v-if="direction === 'horizontal' && hasOverflow">
        <button
          v-for="(item, i) in cloneHead"
          :key="'clone-head-' + i"
          class="video-thumbnail-strip__item"
          tabindex="-1"
          aria-hidden="true"
          @click="onItemClick(i)"
        >
          <div class="video-thumbnail-strip__thumbnail">
            <img
              v-if="item.asset.thumbnailUrl"
              :src="withThumbnailSize(item.asset.thumbnailUrl, 640, 360, 'cover')"
              :alt="item.customTitle || item.asset.name"
              class="video-thumbnail-strip__image"
              loading="lazy"
            />
            <div v-else class="video-thumbnail-strip__placeholder" />
            <span v-if="item.asset.duration" class="video-thumbnail-strip__duration">
              {{ formatTime(item.asset.duration) }}
            </span>
          </div>
          <span class="video-thumbnail-strip__title">
            {{ item.customTitle || item.asset.name }}
          </span>
        </button>
      </template>
    </div>

    <button
      v-if="direction === 'horizontal' && hasOverflow"
      class="video-thumbnail-strip__arrow video-thumbnail-strip__arrow--right"
      aria-label="Next"
      @click="scrollByPage(1)"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import type { VideoAssetListItem } from '../../../types/asset'
import type { CollectionItem } from '../../../types/player'
// 640×360 rather than a square: the tiles are 16:9 boxes cropped by `object-fit: cover`, they are
// 180px wide in the bottom bar and 280px in the drawer, and a canonical 16:9 pair of rungs is what
// keeps `cover` from cropping a still that already has the shape asked for.
import { withThumbnailSize } from '../../../common/thumbnail'
import { formatTime } from '../../utils/time'

const props = defineProps<{
  items: CollectionItem<VideoAssetListItem>[]
  currentIndex: number
  direction: 'horizontal' | 'vertical'
  isPlaying?: boolean
}>()

const emit = defineEmits<{
  select: [index: number]
}>()

const containerRef = ref<HTMLElement | null>(null)
const scrollRef = ref<HTMLElement | null>(null)
const itemRefs = new Map<number, HTMLElement>()
const hasOverflow = ref(false)

// Number of items to clone at each end
const cloneCount = computed(() => Math.min(props.items.length, 6))
const cloneTail = computed(() => props.items.slice(-cloneCount.value))
const cloneHead = computed(() => props.items.slice(0, cloneCount.value))

// Width of one clone group (used to calculate teleport boundaries)
let cloneGroupWidth = 0
let realGroupWidth = 0
let isAdjusting = false

function measureWidths(): void {
  const el = scrollRef.value
  if (!el || props.direction !== 'horizontal') return
  const children = el.children
  const n = props.items.length
  const cn = cloneCount.value
  if (children.length < cn + n + cn) return

  // Clone tail width (first cn children)
  cloneGroupWidth = 0
  for (let i = 0; i < cn; i++) {
    cloneGroupWidth += (children[i] as HTMLElement).offsetWidth
  }
  // Add gaps
  const gap = 10
  cloneGroupWidth += (cn - 1) * gap

  // Real items width
  realGroupWidth = 0
  for (let i = cn; i < cn + n; i++) {
    realGroupWidth += (children[i] as HTMLElement).offsetWidth
  }
  realGroupWidth += (n - 1) * gap
}

function initScrollPosition(): void {
  const el = scrollRef.value
  if (!el || props.direction !== 'horizontal' || !hasOverflow.value) return
  measureWidths()
  // Position scroll so the real items are visible (skip the prepended clones)
  isAdjusting = true
  el.scrollLeft = cloneGroupWidth + 10 // +gap/padding
  isAdjusting = false
}

function checkOverflow(): void {
  const el = scrollRef.value
  if (!el || props.direction !== 'horizontal') return
  // Check overflow on the real items only (before clones are added)
  // If items × ~190px > container width, we have overflow
  const itemWidth = 190 // 180px + 10px gap
  hasOverflow.value = props.items.length * itemWidth > el.clientWidth
}

function onScroll(): void {
  if (isAdjusting || props.direction !== 'horizontal' || !hasOverflow.value) return
  const el = scrollRef.value
  if (!el) return

  const padding = 12
  const gap = 10

  // If scrolled into the prepended clones area, teleport to the end
  if (el.scrollLeft < cloneGroupWidth - realGroupWidth * 0.5) {
    isAdjusting = true
    el.scrollLeft += realGroupWidth + gap
    isAdjusting = false
  }
  // If scrolled into the appended clones area, teleport to the start
  else if (el.scrollLeft > cloneGroupWidth + realGroupWidth - el.clientWidth * 0.5) {
    isAdjusting = true
    el.scrollLeft -= realGroupWidth + gap
    isAdjusting = false
  }
}

function scrollByPage(dir: -1 | 1): void {
  const el = scrollRef.value
  if (!el) return
  const scrollAmount = el.clientWidth * 0.75
  el.scrollBy({ left: dir * scrollAmount, behavior: 'smooth' })
}

function setItemRef(index: number, el: HTMLElement | null): void {
  if (el) {
    itemRefs.set(index, el)
  } else {
    itemRefs.delete(index)
  }
}

function scrollToCurrentItem(): void {
  const itemEl = itemRefs.get(props.currentIndex)
  if (itemEl && scrollRef.value) {
    itemEl.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }
}

// Drag-to-scroll (horizontal only)
let isDragging = false
let dragStartX = 0
let dragScrollLeft = 0
let hasDragged = false

function onMouseDown(e: MouseEvent): void {
  if (props.direction !== 'horizontal') return
  const el = scrollRef.value
  if (!el) return
  isDragging = true
  hasDragged = false
  dragStartX = e.clientX
  dragScrollLeft = el.scrollLeft
  el.style.cursor = 'grabbing'
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

function onMouseMove(e: MouseEvent): void {
  if (!isDragging) return
  e.preventDefault()
  const el = scrollRef.value
  if (!el) return
  const dx = e.clientX - dragStartX
  if (Math.abs(dx) > 3) hasDragged = true
  el.scrollLeft = dragScrollLeft - dx
}

function onMouseUp(): void {
  if (!isDragging) return
  isDragging = false
  const el = scrollRef.value
  if (el) el.style.cursor = ''
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
}

function onItemClick(index: number): void {
  if (hasDragged) return
  emit('select', index)
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  nextTick(() => {
    checkOverflow()
    nextTick(() => {
      initScrollPosition()
      measureWidths()
    })
  })
  if (scrollRef.value) {
    resizeObserver = new ResizeObserver(() => {
      checkOverflow()
      nextTick(() => {
        initScrollPosition()
        measureWidths()
      })
    })
    resizeObserver.observe(scrollRef.value)
  }
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
  resizeObserver?.disconnect()
})

watch(
  () => props.currentIndex,
  () => {
    nextTick(scrollToCurrentItem)
  },
)

watch(
  () => props.items.length,
  () => {
    nextTick(() => {
      checkOverflow()
      nextTick(() => {
        initScrollPosition()
        measureWidths()
      })
    })
  },
)
</script>

<style lang="scss" scoped>
.video-thumbnail-strip {
  background: transparent;
  flex-shrink: 0;
  min-height: 0;
  position: relative;

  // =========================================
  // Horizontal layout (bottom-bar) — carousel
  // =========================================
  &--horizontal {
    // The strip sits beside `.video-player`, not in it, so it is its own container.
    container: vp-strip / inline-size;
    border-top: 1px solid var(--vp-border-default);

    .video-thumbnail-strip__scroll {
      display: flex;
      flex-direction: row;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 10px 12px;
      gap: 10px;
      scrollbar-width: none;
      -ms-overflow-style: none;
      cursor: grab;
      user-select: none;
      -webkit-user-select: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .video-thumbnail-strip__item {
      flex: 0 0 auto;
      width: 180px;
    }

    // A phone's width showed one and a half tiles; this shows two and a half.
    @container vp-strip (max-width: 480px) {
      .video-thumbnail-strip__item {
        width: 136px;
      }
    }
  }

  // =========================================
  // Arrows
  // =========================================
  &__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: var(--vp-backdrop-light);
    color: var(--vp-panel-text-color, #ffffff);
    cursor: pointer;
    transition: background-color 0.15s ease, opacity 0.15s ease;
    backdrop-filter: blur(4px);

    &:hover {
      background: var(--vp-backdrop-medium);
    }

    &--left {
      left: 6px;
    }

    &--right {
      right: 6px;
    }

    // A finger swipes the strip; the arrows only covered the tiles under them.
    @media (hover: none) and (pointer: coarse) {
      display: none;
    }
  }

  // =========================================
  // Vertical layout (sidebar)
  // =========================================
  &--vertical {
    width: 280px;
    overflow: hidden;
    border-left: 1px solid var(--vp-border-default);

    .video-thumbnail-strip__scroll {
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 8px;
      gap: 4px;
      height: 100%;
      scrollbar-width: thin;
      scrollbar-color: var(--vp-scrollbar-color) transparent;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--vp-scrollbar-color);
        border-radius: 3px;
      }
    }

    .video-thumbnail-strip__item {
      width: 100%;
    }
  }

  // =========================================
  // Item
  // =========================================
  &__item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px;
    border: 2px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    background: transparent;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    text-align: left;
    color: inherit;
    font: inherit;

    &:hover {
      background-color: var(--vp-surface-hover);
    }

    &--active {
      // Theme-aware neutral outline that always contrasts with the page
      // background (light on dark themes, dark on light themes) — the primary
      // color is unreliable here: a dark primary on a black page is invisible.
      border-color: var(--vp-text-secondary);
      background-color: var(--vp-surface-active);
    }
  }

  // =========================================
  // Thumbnail
  // =========================================
  &__thumbnail {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 4px;
    overflow: hidden;
    background-color: var(--vp-surface-subtle);
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    -webkit-user-drag: none;
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    background-color: var(--vp-surface-hover);
  }

  &__duration {
    position: absolute;
    bottom: 4px;
    right: 4px;
    background-color: rgba(0, 0, 0, 0.75);
    color: white;
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    padding: 2px 6px;
    border-radius: 4px;
    line-height: 1.2;
  }

  // =========================================
  // Title
  // =========================================
  &__title {
    font-size: 0.8rem;
    color: var(--vp-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.3;
    padding: 0 2px;

    .video-thumbnail-strip__item--active & {
      color: var(--vp-text-primary);
      font-weight: 600;
    }
  }

  // =========================================
  // Now Playing equalizer (active thumbnail overlay)
  // =========================================
  &__now-playing {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    background-color: rgba(0, 0, 0, 0.35);
  }

  &__eq-bar {
    display: block;
    width: 3px;
    height: 16px;
    background-color: white;
    border-radius: 1px;
    animation: vts-equalizer 0.8s ease-in-out infinite alternate;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes vts-equalizer {
  0% {
    transform: scaleY(0.3);
  }
  100% {
    transform: scaleY(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .video-thumbnail-strip__eq-bar {
    animation: none;
    transform: scaleY(0.7);
  }
}
</style>
